import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import './ProductsOverview.css';

function ProductsOverview() {
  const { t } = useTranslation('products');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    products: { total: 0, active: 0, with_boms: 0, without_boms: 0 },
    product_types: { total: 0, breakdown: [] },
    boms: { total: 0, active: 0 },
    pricing: { avg_price: 0, min_price: 0, max_price: 0 },
    lead_time: { avg_days: 0 },
    inventory: { low_stock_count: 0 }
  });

  // Fetch products metrics
  useEffect(() => {
    fetchProductsMetrics();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchProductsMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchProductsMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/products/metrics/overview'));
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching products metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="products-overview">
      {/* Key Metrics Cards - 2 rows of 3 */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">📦</span>
            <span className="kpi-label">{t('overview.kpi.totalProducts')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.products.total}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{metrics.products.active} {t('overview.kpi.active')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">🏷️</span>
            <span className="kpi-label">{t('overview.kpi.productTypes')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.product_types.total}</div>
          <div className="kpi-footer">
            <div className="kpi-change">
              {!loading && metrics.product_types.breakdown.length > 0
                ? t('overview.kpi.top', { name: metrics.product_types.breakdown[0].type_name })
                : '-'}
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">📋</span>
            <span className="kpi-label">{t('overview.kpi.productsWithBoms')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : `${metrics.products.with_boms}/${metrics.products.total}`}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{metrics.boms.total} {t('overview.kpi.totalBoms')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">💰</span>
            <span className="kpi-label">{t('overview.kpi.avgPrice')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : formatCurrency(metrics.pricing.avg_price)}</div>
          <div className="kpi-footer">
            <div className="kpi-change">
              {formatCurrency(metrics.pricing.min_price)} - {formatCurrency(metrics.pricing.max_price)}
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">⏱️</span>
            <span className="kpi-label">{t('overview.kpi.avgLeadTime')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : `${metrics.lead_time.avg_days} ${t('overview.kpi.days')}`}</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">⚠️</span>
            <span className="kpi-label">{t('overview.kpi.materialAlerts')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.inventory.low_stock_count}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('overview.kpi.lowStockItems')}</div>
          </div>
        </div>
      </div>

      {/* Product Type Breakdown */}
      {!loading && metrics.product_types.breakdown.length > 0 && (
        <div className="customers-analytics">
          <div className="section-header">
            <span className="section-icon">📊</span>
            <h3>{t('overview.sections.productTypeDistribution')}</h3>
          </div>
          <div className="customers-table">
            {metrics.product_types.breakdown.map((type) => (
              <div key={type.type_name} className="customer-row">
                <div className="customer-details">
                  <span className="customer-name">{type.type_name}</span>
                </div>
                <div className="orders-count">{type.count} {t('overview.labels.products')}</div>
                <div className="revenue-amount">
                  {Math.round((type.count / metrics.products.total) * 100)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsOverview;